import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowUpFromLine, Clock, AlertTriangle, ChevronDown, Shield, Wallet, X, Ban, Landmark, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { withdrawAPI, walletAPI, spreadAPI, bankAPI } from '@/lib/api';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const formatINR = (v) => v == null ? '₹0.00' : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v);

export default function WithdrawPage() {
  const { user, refreshUser } = useAuth();
  const [balance, setBalance] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [form, setForm] = useState({ amount: '', bank_account_id: '' });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRestrictionPopup, setShowRestrictionPopup] = useState(false);
  const [restrictions, setRestrictions] = useState(null);

  useEffect(() => {
    walletAPI.getBalance().then(r => setBalance(r.data)).catch(() => {});
    withdrawAPI.getHistory().then(r => setWithdrawals(r.data)).catch(() => {});
    bankAPI.getAll().then(r => {
      setBankAccounts(r.data || []);
      const primary = (r.data || []).find(a => a.is_primary);
      if (primary) setForm(f => ({ ...f, bank_account_id: primary.id }));
      else if ((r.data || []).length === 1) setForm(f => ({ ...f, bank_account_id: r.data[0].id }));
    }).catch(() => {});
  }, []);

  const inrRate = balance?.usd_to_inr_rate || 91.0;
  const amountUSD = parseFloat(form.amount) || 0;
  const amountINR = amountUSD * inrRate;

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) < 10) { toast.error('Minimum withdrawal is $10'); return; }
    if (!form.bank_account_id) { toast.error('Please select a bank account'); return; }
    if (!agreeTerms) { toast.error('Please agree to the terms'); return; }

    // Check restrictions first (popup if blocked)
    try {
      const { data } = await withdrawAPI.checkRestrictions();
      if (!data.can_withdraw) {
        setRestrictions(data);
        setShowRestrictionPopup(true);
        return;
      }
    } catch (err) {
      toast.error('Failed to check withdrawal eligibility');
      return;
    }

    // Proceed with withdrawal
    setLoading(true);
    try {
      await withdrawAPI.submit({ amount: parseFloat(form.amount), bank_account_id: form.bank_account_id });
      toast.success('Withdrawal request submitted!');
      setForm({ amount: '', bank_account_id: form.bank_account_id });
      setAgreeTerms(false);
      refreshUser();
      walletAPI.getBalance().then(r => setBalance(r.data)).catch(() => {});
      withdrawAPI.getHistory().then(r => setWithdrawals(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="withdraw-page">
        <div>
          <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Withdraw Funds</h1>
          <p className="text-xs text-[var(--text-secondary)] mt-1">Request a withdrawal directly to your linked bank account</p>
        </div>

        {/* Balance Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="withdraw-balance">
            <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Available Balance</span>
            <p className="text-2xl font-bold font-mono text-[var(--text-primary)] mt-2">{formatCurrency(balance?.balance ?? user?.wallet_balance)}</p>
            <p className="text-xs font-mono text-green-400 mt-1" data-testid="balance-inr">{formatINR((balance?.balance ?? user?.wallet_balance ?? 0) * inrRate)}</p>
          </div>
          {/* <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">In Trades</span>
            <p className="text-2xl font-bold font-mono text-amber-400 mt-2">{formatCurrency(balance?.total_invested ?? 0)}</p>
            <p className="text-xs font-mono text-amber-400/70 mt-1">{formatINR((balance?.total_invested ?? 0) * inrRate)}</p>
          </div> */}
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Leverage Fund</span>
            <p className="text-2xl font-bold font-mono text-blue-400 mt-2">{formatCurrency(balance?.leverage_fund ?? 0)}</p>
            <p className="text-xs font-mono text-blue-400/70 mt-1">{formatINR((balance?.leverage_fund ?? 0) * inrRate)}</p>
          </div>
        </div>

        {/* Withdrawal Form */}
        <div className="p-6 bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--text-primary)]">New Withdrawal Request</h2>
            <Link to="/bank-accounts" data-testid="manage-banks-link" className="text-xs font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Manage bank accounts
            </Link>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5 max-w-sm">
              <Label className="text-xs text-[var(--text-secondary)]">Withdrawal Amount (USD)</Label>
              <Input type="number" step="any" placeholder="Min $10.00" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="withdraw-amount"
                className="font-mono" />
              {amountUSD > 0 && (
                <div className="flex items-center justify-between px-1" data-testid="withdraw-inr-conversion">
                  <span className="text-xs text-[var(--text-tertiary)]">Approx. INR</span>
                  <span className="text-sm font-mono font-semibold text-emerald-600 dark:text-emerald-400">{formatINR(amountINR)}</span>
                </div>
              )}
              <p className="text-[10px] text-[var(--text-tertiary)] px-1">Rate: 1 USD = {formatINR(inrRate)}</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-[var(--text-secondary)]">Withdrawal Method — Bank Account</Label>
              {bankAccounts.length === 0 ? (
                <div className="p-4 rounded-xl border border-dashed border-[var(--border-default)] bg-[var(--bg-muted)] flex items-center justify-between" data-testid="no-bank-accounts">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Landmark className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[var(--text-primary)]">No bank account linked</p>
                      <p className="text-xs text-[var(--text-secondary)]">You must add a bank account to withdraw funds.</p>
                    </div>
                  </div>
                  <Link to="/bank-accounts" data-testid="add-bank-cta" className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> Add bank
                  </Link>
                </div>
              ) : (
                <div className="space-y-2" data-testid="bank-account-selector">
                  {bankAccounts.map((a) => {
                    const selected = form.bank_account_id === a.id;
                    return (
                      <button
                        type="button"
                        key={a.id}
                        onClick={() => setForm({ ...form, bank_account_id: a.id })}
                        data-testid={`bank-option-${a.id}`}
                        className={`w-full text-left p-4 rounded-xl border transition-all flex items-center gap-3 ${
                          selected
                            ? 'border-emerald-500 bg-emerald-500/8 ring-2 ring-emerald-500/20'
                            : 'border-[var(--border-default)] bg-[var(--bg-surface)] hover:border-[var(--text-tertiary)]'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                          <Landmark className="w-5 h-5 text-cyan-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-[var(--text-primary)]">{a.bank_name}</p>
                            {a.is_primary && <span className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded font-semibold">Primary</span>}
                            <span className="text-[10px] bg-[var(--hover-bg)] text-[var(--text-secondary)] px-1.5 py-0.5 rounded capitalize">{a.account_type}</span>
                          </div>
                          <p className="text-xs text-[var(--text-secondary)]">{a.account_holder}</p>
                          <p className="text-xs font-mono text-[var(--text-tertiary)]">****{a.account_number?.slice(-4)} · {a.ifsc_code}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-emerald-500 bg-emerald-500' : 'border-[var(--border-default)]'}`}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} data-testid="withdraw-agree"
              className="w-4 h-4 rounded border-[var(--border-default)]" />
            <span className="text-xs text-[var(--text-secondary)]">I agree that withdrawals are processed within 24-48 hours and are subject to verification.</span>
          </div>
          <button onClick={handleSubmit} disabled={loading || bankAccounts.length === 0} data-testid="withdraw-submit-btn"
            className="w-full py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            {loading ? 'Submitting...' : bankAccounts.length === 0 ? 'Link a bank account to continue' : 'Submit Withdrawal Request'}
          </button>
        </div>

        {/* Withdrawal History */}
        {withdrawals.length > 0 && (
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--border-subtle)]">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Withdrawal History</h3>
            </div>
            <div className="divide-y divide-[var(--border-subtle)]">
              {withdrawals.map(w => (
                <div key={w.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${w.status === 'approved' ? 'bg-emerald-400' : w.status === 'pending' ? 'bg-amber-400' : 'bg-red-400'}`} />
                    <div>
                      <p className="text-sm font-mono text-[var(--text-primary)]">{formatCurrency(w.amount)}</p>
                      <p className="text-[10px] text-[var(--text-tertiary)]">{w.method} • {w.created_at ? new Date(w.created_at).toLocaleDateString('en-IN') : ''}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium capitalize ${w.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : w.status === 'pending' ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400'}`}>{w.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Restriction Popup Modal */}
      {showRestrictionPopup && restrictions && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="restriction-popup">
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl p-6 max-w-md w-full space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-400" />
                <h3 className="text-base font-bold text-[var(--text-primary)]">Withdrawal Blocked</h3>
              </div>
              <button onClick={() => setShowRestrictionPopup(false)} className="p-1 rounded hover:bg-[var(--hover-bg)] text-[var(--text-tertiary)]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[var(--text-secondary)]">Your withdrawal cannot be processed due to the following outstanding obligations:</p>
            <div className="space-y-3">
              {restrictions.blocks.map((block, i) => (
                <div key={i} className={`p-4 rounded-lg border ${block.type === 'leverage' ? 'bg-blue-500/5 border-blue-500/20' : 'bg-amber-500/5 border-amber-500/20'}`} data-testid={`restriction-block-${block.type}`}>
                  <p className={`text-sm font-semibold ${block.type === 'leverage' ? 'text-blue-400' : 'text-amber-400'}`}>{block.title}</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">{block.message}</p>
                  {block.type === 'leverage' && (
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--text-tertiary)]">Leverage Balance</span>
                        <span className="font-mono text-[var(--text-primary)]">{formatCurrency(block.leverage_balance)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--text-tertiary)]">Used in Trades</span>
                        <span className="font-mono text-amber-400">{formatCurrency(block.leverage_used)}</span>
                      </div>
                      {block.remaining_to_use > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-[var(--text-tertiary)]">Remaining to Use</span>
                          <span className="font-mono text-blue-400">{formatCurrency(block.remaining_to_use)} <span className="text-blue-400/70">({formatINR(block.remaining_to_use_inr)})</span></span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--text-tertiary)]">Repaid</span>
                        <span className="font-mono text-emerald-400">{formatCurrency(block.repaid)}</span>
                      </div>
                      {block.outstanding > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-[var(--text-tertiary)]">Outstanding Repayment</span>
                          <span className="font-mono font-semibold text-red-400">{formatCurrency(block.outstanding)} <span className="text-red-400/70">({formatINR(block.outstanding_inr)})</span></span>
                        </div>
                      )}
                    </div>
                  )}
                  {block.type === 'spread' && (
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--text-tertiary)]">Unpaid Spread</span>
                        <span className="font-mono font-semibold text-red-400">{formatCurrency(block.total_unpaid)} <span className="text-amber-400/70">({formatINR(block.total_unpaid_inr)})</span></span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--text-tertiary)]">Trades with Spread</span>
                        <span className="font-mono text-[var(--text-secondary)]">{block.unpaid_trades}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[var(--text-tertiary)] text-center">Contact support or admin to resolve these restrictions.</p>
            <button onClick={() => setShowRestrictionPopup(false)} data-testid="restriction-popup-close"
              className="w-full py-2.5 bg-[var(--hover-bg)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--border-subtle)] transition-colors">
              Close
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
